import React from "react";
import { AlertTriangle, Info, X } from "lucide-react";

type ConfirmModalProps = {
  isOpen: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  secondaryLabel?: string;
  tone?: "danger" | "default";
  confirmDisabled?: boolean;
  secondaryDisabled?: boolean;
  onConfirm: () => void;
  onSecondary?: () => void;
  onCancel: () => void;
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  description,
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  secondaryLabel,
  tone = "default",
  confirmDisabled = false,
  secondaryDisabled = false,
  onConfirm,
  onSecondary,
  onCancel,
}) => {
  if (!isOpen) return null;

  const isDanger = tone === "danger";
  const Icon = isDanger ? AlertTriangle : Info;
  const confirmClasses = isDanger
    ? "btn bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
    : "btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDanger ? "bg-red-100 dark:bg-red-900/30" : "bg-blue-100 dark:bg-blue-900/30"}`}>
              <Icon className={`h-5 w-5 ${isDanger ? "text-red-600 dark:text-red-400" : "text-blue-600 dark:text-blue-400"}`} />
            </div>
            <div>
              <h2 id="confirm-modal-title" className="text-xl font-semibold text-slate-900 dark:text-white">
                {title}
              </h2>
              {description && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</p>}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          {cancelLabel && (
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onSecondary}
              disabled={secondaryDisabled}
            >
              {secondaryLabel}
            </button>
          )}
          <button type="button" className={confirmClasses} onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
